import React from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "../components/ui/breadcrumb"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"
import type { CogoElement } from "../core/types"
import { readResolvedArray, recordValue, resolvedText, safeNavigationUrl, shorthandId } from "./property-value-utils"

export function BreadcrumbRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("breadcrumb")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  const explicitItems = element.children ?? []
  // JITRULE Task 2: authored children skip the recordValue() shorthand path
  // below, so lift them directly — same rule, single interpreter.
  const items = explicitItems.length > 0
    ? explicitItems.map(liftV4Props)
    : readResolvedArray(element, "items", resolveSymbol).map((raw, index) => {
        const item = recordValue(raw)
        return {
          ...item,
          id: String(item.id ?? shorthandId(element.id, "items", index)),
          type: "link",
          text: item.label ?? item.text,
          href: item.href,
        } as unknown as CogoElement
      })

  return (
    <Breadcrumb
      className={cn(stripPresetConflicts(element.className, undefined, element.designAuthorityClassName))}
      data-testid={`breadcrumb-${element.id}`}
      data-atom-type="breadcrumb"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    >
      <BreadcrumbList>
        {items.map((child, i) => {
          const isLast = i === items.length - 1
          const label = resolvedText(child.text ?? child.label, resolveSymbol)
          const rawHref = resolvedText(child.href, resolveSymbol)
          const href = safeNavigationUrl(rawHref)

          return (
            <React.Fragment key={child.id}>
              {i > 0 && <BreadcrumbSeparator />}
              <BreadcrumbItem>
                {isLast ? (
                    <BreadcrumbPage>{label}</BreadcrumbPage>
                  ) : href ? (
                    <BreadcrumbLink href={href}>{label}</BreadcrumbLink>
                  ) : child.type === "button" ? (
                    <BreadcrumbLink
                      render={
                        <button
                          type="button"
                          onClick={() => dispatchEvent(child.id, "tap", { value: label })}
                        />
                      }
                    >
                      {label}
                    </BreadcrumbLink>
                ) : (
                  <ElementRenderer element={child} />
                )}
              </BreadcrumbItem>
            </React.Fragment>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
